import { useEffect, useState } from "react";
import { Download, Share, X, Plus, MoreVertical, AlertTriangle } from "lucide-react";

function isIOS() {
  const ua = window.navigator.userAgent;
  return (/iphone|ipad|ipod/i.test(ua) && !window.MSStream) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function isAndroid() { return /android/i.test(window.navigator.userAgent); }
function isInAppBrowser() {
  const ua = window.navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Line\/|Twitter|Messenger|MicroMessenger|; wv\)|FB_IAB/i.test(ua);
}
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export const InstallButton = ({ className = "btn btn-outline", label = "Installer l'app", size = 16, always = false }) => {
  const [deferred, setDeferred] = useState(null);
  const [help, setHelp] = useState(null); // "ios" | "android" | "desktop" | "inapp"
  const [installed, setInstalled] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    if (window.__pwaPrompt) setDeferred(window.__pwaPrompt);
    const onPrompt = (e) => { e.preventDefault(); window.__pwaPrompt = e; setDeferred(e); };
    const onCaptured = () => { if (window.__pwaPrompt) setDeferred(window.__pwaPrompt); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); window.__pwaPrompt = null; };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("pwa-installable", onCaptured);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("pwa-installable", onCaptured);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  if (!deferred && !ios && !always) return null;

  const click = async () => {
    if (deferred) {
      // Invite d'installation NATIVE (demande d'autorisation du navigateur)
      deferred.prompt();
      try {
        const res = await deferred.userChoice;
        if (res && res.outcome === "accepted") { setDeferred(null); window.__pwaPrompt = null; return; }
      } catch (e) { console.debug("Invite d'installation ignorée", e); }
      setDeferred(null);
      return;
    }
    if (isInAppBrowser()) { setHelp("inapp"); return; }
    if (ios) { setHelp("ios"); return; }
    if (isAndroid()) { setHelp("android"); return; }
    setHelp("desktop");
  };

  const close = () => setHelp(null);
  const Modal = ({ title, children }) => (
    <div className="modal-overlay" onClick={close} data-testid="install-help-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400, textAlign: "center" }}>
        <button className="modal-close" onClick={close}><X size={18} /></button>
        <div className="mc-ico" style={{ margin: "0 auto 14px" }}><Download size={22} /></div>
        <h3 style={{ margin: "0 0 10px", fontSize: 18 }}>{title}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.65, margin: "0 0 14px" }}>
          Ajoutez PROGÈRE PUNCH à votre écran d'accueil pour <b>puncher en un geste</b>. Vos pointages se
          <b> synchronisent automatiquement</b> avec PROGÈRE PUNCH (et fonctionnent hors ligne).
        </p>
        <div style={{ fontSize: 13.5, lineHeight: 1.8, textAlign: "left", color: "var(--text)" }}>{children}</div>
        <button className="btn btn-gold" style={{ marginTop: 18, width: "100%" }} onClick={close}>Compris</button>
      </div>
    </div>
  );

  return (
    <>
      <button className={className} onClick={click} data-testid="install-app-btn">
        <Download size={size} /> {label}
      </button>

      {help === "ios" && (
        <Modal title="Installer sur iPhone / iPad">
          1. Ouvrez ce site dans <b>Safari</b> (obligatoire sur iPhone).<br />
          2. Touchez l'icône <Share size={14} style={{ verticalAlign: "-2px" }} /> <b>Partager</b> en bas.<br />
          3. Choisissez <b>« Sur l'écran d'accueil »</b> <Plus size={13} style={{ verticalAlign: "-1px" }} />.<br />
          4. Touchez <b>Ajouter</b>. L'app apparaît sur votre écran d'accueil.
        </Modal>
      )}
      {help === "android" && (
        <Modal title="Installer sur Android">
          1. Touchez le menu <MoreVertical size={14} style={{ verticalAlign: "-2px" }} /> (en haut à droite de Chrome).<br />
          2. Choisissez <b>« Installer l'application »</b> ou <b>« Ajouter à l'écran d'accueil »</b>.<br />
          3. Confirmez avec <b>Installer</b>.<br />
          <span style={{ color: "var(--muted)", fontSize: 12 }}>Si l'option n'apparaît pas, l'app est peut-être déjà installée, ou ouvrez le site dans <b>Chrome</b>.</span>
        </Modal>
      )}
      {help === "desktop" && (
        <Modal title="Installer sur ordinateur">
          Cliquez sur l'icône d'installation <Download size={13} style={{ verticalAlign: "-1px" }} /> à droite de la barre d'adresse
          (Chrome / Edge), puis <b>Installer</b>.
        </Modal>
      )}
      {help === "inapp" && (
        <Modal title="Ouvrez dans votre navigateur">
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "var(--gold)", marginBottom: 8 }}>
            <AlertTriangle size={16} style={{ flex: "0 0 auto", marginTop: 2 }} />
            <span>Vous utilisez un navigateur intégré (Facebook, Instagram, Messenger…) qui n'autorise pas l'installation.</span>
          </div>
          Touchez le menu <MoreVertical size={14} style={{ verticalAlign: "-2px" }} /> puis <b>« Ouvrir dans le navigateur »</b> (Chrome / Safari), et relancez l'installation depuis là.
        </Modal>
      )}
    </>
  );
};
